import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import Logo1 from '../../assets/Logo-RB.png';
import Back from '../../assets/BackG.png';

const glass = css`
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.35);
`;

const HeroContainer = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 6.5rem 1.25rem 3rem; /* leaves space for fixed navbar */
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: url(${Back}) no-repeat center center;
    background-size: cover;
    z-index: -2;
    transform: scale(1.02);
  }

  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.36);
    z-index: -1;
  }
`;

const ContentCard = styled.div`
  ${glass}
  width: min(1100px, 92vw);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.10);
  padding: 2.4rem 2.2rem;
  text-align: center;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem;
    border-radius: 22px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.75rem;
`;

const Logo = styled.img`
  height: 240px;
  width: auto;
  transition: transform 0.25s ease, filter 0.25s ease;

  &:hover {
    transform: scale(1.04);
    filter: brightness(1.08);
  }

  @media (max-width: 768px) {
    height: 180px;
  }
`;

const Title = styled.h1`
  margin: 0.2rem 0 0.5rem;
  font-size: clamp(2.6rem, 5vw, 4.6rem);
  font-weight: 800;
  font-family: 'Chau Philomene One', sans-serif;
  color: rgba(255, 255, 255, 0.96);
  text-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
`;

const Subtitle = styled.p`
  margin: 0 auto;
  max-width: 980px;
  font-size: clamp(1.15rem, 2vw, 1.75rem);
  color: rgba(255, 255, 255, 0.92);
  font-family: 'Chau Philomene One', sans-serif;
  line-height: 1.55;
  text-shadow: 0 8px 20px rgba(0, 0, 0, 0.35);

  .highlight {
    font-weight: 900;
    color: rgba(120, 255, 140, 0.95);
  }
`;

const ScopeBanner = styled.p`
  margin: 0.75rem auto 0;
  max-width: 780px;
  font-size: clamp(0.85rem, 1.3vw, 1rem);
  color: rgba(255, 255, 255, 0.78);
  font-family: 'Poppins', sans-serif;
  line-height: 1.5;
  font-style: italic;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.7rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  ${glass}
  border-radius: 999px;
  padding: 0.85rem 2rem;
  font-size: 1rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  cursor: pointer;

  background: rgba(255, 255, 255, 0.65);
  color: rgba(10, 30, 10, 0.92);
  transition: transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(19, 111, 7, 0.78);
    color: #fff;
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
  }

  &:active {
    transform: translateY(0px) scale(0.98);
  }
`;

const SecondaryButton = styled(Button)`
  background: rgba(255, 255, 255, 0.30);
  color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.35);

  &:hover {
    background: rgba(255, 255, 255, 0.55);
    color: rgba(10, 30, 10, 0.92);
  }
`;

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <HeroContainer>
      <ContentCard>
        <LogoContainer>
          <Logo src={Logo1} alt="MAKASA Program Logo" />
        </LogoContainer>

        <Title>M.A.K.A.S.A. Program</Title>

        <Subtitle>
          Modeling Agricultural Knowledge using AI for Sustainable Agriculture
        </Subtitle>

        <ScopeBanner>
          Current Dissertation Focus: Irrigation — implemented through the LISA project under the MAKASA Program.
        </ScopeBanner>

        <ButtonContainer>
          <Button onClick={() => navigate('/about')}>View Program Roadmap</Button>
          <SecondaryButton onClick={() => window.open('https://lisa.bisubilar.org/', '_blank')}>
            Open LISA (Irrigation Project)
          </SecondaryButton>
        </ButtonContainer>
      </ContentCard>
    </HeroContainer>
  );
};

export default HeroSection;
